import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
  EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
  EXECUTION_BLUEPRINT_LOCKED_DEFAULT,
} from "../../../../data/RouteUrls";
import {
  CheckIcon,
  CloseIcon,
  TrashIcon,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import { ElegantTooltip } from "../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
import ConfirmStepModal, {
  CONFIRM_YEAR_ONE_PLAN,
} from "../ExecutionBlueprintWigConnections/ConfirmStepModal";
import liveReviewSocketStore, {
  useLiveReviewSocket,
  LIVE_REVIEW_STEP,
  PARTICIPANT_ROLE,
  isBlueprintLocked,
} from "../ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore";
import {
  buildYearOneGroups,
  buildRoster,
  initialsFor,
  countShortlisted,
  isShortlistEmpty,
} from "../ExecutionBlueprintLiveReview/_shared/yearOnePlan";
// The section accordion, its collapsed summaries and its rows — shared with
// the other app's copy of this screen AND with the locked view's Year 1 tab.
import YearOnePlanSections from "../ExecutionBlueprintLiveReview/_shared/YearOnePlanSections";
// Add / remove a WIG — handoff #25 §2. Same two components as stages 1 and 2.
import AddWigRow, {
  canDeleteLiveReviewItem,
} from "../ExecutionBlueprintLiveReview/_shared/AddWigRow";
import DeleteWigDialog from "../ExecutionBlueprintLiveReview/_shared/DeleteWigDialog";
// The shared skeleton chip — one shimmer across every live-review screen.
import EbSkeleton from "../ExecutionBlueprintLiveReview/_shared/EbSkeleton";
import { useExecutionBlueprintStoreSelector } from "../_redux/ExecutionBlueprintStore";
import YearOnePlanLoading from "./YearOnePlanLoading";
import { redirectForStep } from "../_redux/liveReviewStepRoutes";
import "./YearOnePlan.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Year 1 Plan - Figma node 33860:43427. Stage 3, and the END of the Execution
// Blueprint journey (Blueprint Review → WIG Connections → Year 1 Plan).
//
// **LIVE as of 2026-08-29.** Built design-only against a hand-written mock;
// handoff v3's §6b then shipped the whole stage — the shortlist agent,
// `set_item_shortlist`, `set_item_owner` and `confirm_year1_plan`. The mock
// (`YearOnePlanData.js`) is DELETED, and everything on screen is the live
// snapshot's.
//
// ⚠️ IT IS A STEP, NOT A DESTINATION. Nothing links here: both parties are
// routed in by `live_review_step_changed`, and this screen bounces anyone whose
// step says they belong elsewhere. It joins the socket itself because §6b.3's
// "you get an email instead" case lands someone here cold, and a reload is the
// same case.
//
// ⚠️ TWO LOCKS, AND THIS SCREEN IS WHERE THE SECOND ONE LIVES.
//   - "Lock Blueprint" (§6a) froze item VALUES. It has already happened by the
//     time anyone is here — that is what `year1_review` means.
//   - "Confirm Year 1 Plan" (§6b.7) is the REAL, TERMINAL lock: the whole
//     blueprint becomes `LOCKED` and read-only, permanently.
// §6b.7 also says there is **no server-side "everything must be reviewed" gate**
// — so the confirmation dialog here is genuinely the only thing standing between
// a stray click and a permanently locked blueprint. Do not remove it, and do not
// let it be dismissed by an outside click without a deliberate choice.
//
// Three contract details that shape what you see:
//   - **No expected shortlist count.** §6b.4: the agent uses each item's
//     connector count as its leverage signal and guarantees at least one per
//     section, but there is no target number. So nothing here says "pick 6".
//   - **An empty shortlist is a real answer**, not an error: a twice-failed
//     agent returns every item unshortlisted with no owner, and both parties
//     build it by hand from there. That gets a different line of copy, not an
//     error state.
//   - **`owner_suggested_by_agent` is a styling hint only.** Any deliberate pick
//     clears it server-side, even re-picking the agent's own suggestion, because
//     that counts as a human decision (§6b.6).
//
// ⚠️ REDESIGNED 2026-09-03 ("means redesign whole one year plan screen"), and
// the reasoning for each change sits at its own call site below. In short: three
// headers became one, the AI paragraph became one line, the row's checkbox
// became a labelled button, the WIG text wraps instead of truncating, the
// derived priority number is gone, and a COLLAPSED section now summarises its
// contribution to the plan rather than counting its contents — which is what
// makes the all-shut state readable. The list stayed a list rather than becoming
// cards: every row holds the same three facts and the reader compares them down
// a column, which is the one job cards are worse at.
//
// The design's priority number is DERIVED from position (see the shared module);
// the server has no such field, so it was display shorthand and nothing more.
// It is still derived and no longer drawn.
// ─────────────────────────────────────────────────────────────────────────────

const selectClients = (state) => state.clients;

const selectSnapshot = (state) => state.snapshot;
const selectJoined = (state) => state.joined;
const selectJoining = (state) => state.joining;
const selectLastError = (state) => state.lastError;
const selectPendingItems = (state) => state.pendingItems;
const selectAddPending = (state) => state.addPending;
const selectLocking = (state) => state.locking;
// Read only to hand to the drafting wait — a socket that never joined looks
// identical to an agent still working, since the wait's bar and steps are on
// timers either way.
const selectJoinError = (state) => state.joinError;

// The steps this screen is the right home for. Everything else routes away
// via the shared table in `_redux/liveReviewStepRoutes.js` —
// BOTH year1 steps: this screen owns its own drafting wait.
const OWN_STEPS = [
  LIVE_REVIEW_STEP.YEAR1_DRAFTING,
  LIVE_REVIEW_STEP.YEAR1_REVIEW,
];

// The design's own glyphs, kept as the characters they are in the Figma layer
// tree rather than substituted with SVGs — those icons are TEXT nodes on that
// node containing one of these.
//
// ⚠️ `person` USED TO LIVE HERE and no longer does — see `PersonIcon` below.
// The rule above holds for a glyph that is a plain character; it broke down for
// the one that was an emoji.
const GLYPH = {
  sparkle: "✦",
  arrowLeft: "←",
};

// The unassigned owner's avatar.
//
// ⚠️ An inline SVG, replacing the `👤` emoji this rendered until 2026-09-02 (on
// instruction — the unassigned cell "is not looking good"). The emoji was the
// whole problem, and in a way that could only ever be seen on screen: an emoji
// paints in its OWN colours, so `.yop-avatar.is-empty`'s `color: $yop-muted`
// never reached it. A full-colour platform figure sat inside a pale grey circle
// — a different picture on macOS, Windows and Android, none of them the muted
// placeholder the design asks for, and none of them sitting comfortably beside
// the flat two-letter initials of the assigned state in the very next row. Its
// glyph metrics also ignore `line-height`, so it rode off the circle's centre.
//
// This is the first SVG on this screen, so it departs from the GLYPH-map
// convention above deliberately. The same call was made once before, for the ⋯
// mark on the connector card, for the same reason: a text glyph at small size
// reads as punctuation left in the markup by accident. (That card's header was
// removed on 2026-09-03, so this is now the only place the reasoning survives.)
//
// Stroked with `currentColor`, so the circle's own `color` genuinely drives it
// and the disabled state's `opacity` inherits for free. Kept local to this file
// rather than imported: `YearOnePlan.js` is a cross-repo near-twin and the two
// apps keep their icon modules at different paths, so an import is a difference
// that has to be maintained forever.
const PersonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="8.5" r="3.6" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M4.8 19.8c0-3.5 3.3-5.7 7.2-5.7s7.2 2.2 7.2 5.7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

// Static UI copy, not data. The banner explains what the agent did, so it lives
// with the screen rather than coming off the wire.
//
// ⚠️ ONE LINE EACH, and cut down to that on 2026-09-03 on instruction ("redesign
// and refine copy of yop-ai which need to be shorter and good"). Both used to be
// a bold title, an "AI" pill and a four-line paragraph. The paragraph explained
// what leverage means, which is the shortlist agent's reasoning rather than
// anything the reader has to act on — and it pushed the first WIG below the
// fold. What survives is the only sentence that changes behaviour: this was
// written by AI, and you may overrule it.
//
// Split into `lead` + `text` because the lead is the part that must survive a
// glance, so it is bold and it comes first. The pill is gone with the title: a
// teal "AI" chip beside the words "AI-suggested" said the same thing twice.
const AI_BANNER = {
  lead: "AI coach suggestion.",
  text: "The AI picked the highest-leverage WIGs and proposed an owner for each. Change anything before you lock it.",
};

// The twice-failed-agent case (§6b.4). Still not an error: it is a real answer
// that happens to be empty, so it gets an instruction rather than an apology.
const EMPTY_BANNER = {
  lead: "Nothing prioritised yet.",
  text: "Pick the WIGs that matter most for the next 12 months and give each one an owner. You and the CEO can do this together now.",
};

// The blueprint's four WIG sections — Financial, Customer, Operations, People.
// Fixed by the structure itself, which is why the loading skeleton below can
// draw exactly the right number of cards rather than guessing at a count.
const SKELETON_SECTIONS = [0, 1, 2, 3];

// ── The two waits, and why they are not the same wait ───────────────────────
// ⚠️ SPLIT ON 2026-09-03. Until now one bare spinner covered both, which is
// what the instruction asked for at the time (2026-08-31). It now covers only
// one of them:
//
//   `year1_drafting`  → `YearOnePlanLoading`, the designed generating screen.
//                       An AGENT RUN of unknown length. It deserves to say what
//                       is happening, and §6b.2 asked for a wait "of the same
//                       shape as the connectors loading screen" — which is what
//                       it now is, sharing that screen's chrome outright.
//   anything else     → the spinner below. A socket handshake, or a snapshot
//                       that has not landed. Sub-second, and nothing truthful
//                       can be said about it — claiming "AI is generating your
//                       plan" while merely connecting would be a lie the first
//                       time it appeared on a slow network.
//
// ⚠️ THE SECOND ONE IS NOW A SKELETON, NOT A SPINNER (2026-09-03, on
// instruction: "loading screen on year one plan maybe better if we can do
// that"). The note that used to sit here said a row skeleton "implies a shape
// the shortlist may not have, and it sat under a header and column headings
// describing an empty table". Both halves of that objection are gone:
//
//   - The column headings ARE gone — `.yop-col-head` was deleted in the
//     redesign, so there is no longer a table header describing nothing.
//   - It no longer implies a shortlist shape, because it does not draw rows.
//     It draws the four SECTION CARDS, which are the one thing about this
//     screen that is knowable before the data arrives: every blueprint has the
//     same four WIG sections, whatever the agent shortlists inside them. Their
//     summaries are left blank precisely because those are not knowable.
//
// So this is the house rule finally applied here — CLAUDE.md: "Skeletons mirror
// real layout, not a spinner." A centred ring told the reader nothing and
// changed the page's whole geometry the instant the plan landed; this holds the
// header, the note strip and the four cards in their real positions, so the
// arrival is content filling a frame that was already correct.
const YearOnePlanConnecting = () => (
  // `aria-label` rather than visually-hidden text: neither app ships an
  // `.sr-only` utility, so a hidden-text span would simply render as visible
  // copy — which is the one thing this screen is not supposed to have. The
  // chips inside are each `aria-hidden`, so this label is the only thing
  // announced.
  <div
    id="year-one-plan"
    role="status"
    aria-busy="true"
    aria-label="Loading your prioritised WIGs"
  >
    {/* Same `.yop-head` the real screen uses, so the title block and the count
        chip do not move when the plan arrives. */}
    <div className="yop-head">
      <div className="yop-head-text">
        <EbSkeleton className="yop-skel-title" strong />
        <EbSkeleton className="yop-skel-subtitle" />
      </div>
      <EbSkeleton className="yop-skel-count" />
    </div>

    <div className="yop-scroll">
      {/* The AI note strip. Tinted rather than grey — it is the one element on
          this screen whose colour is known in advance, and leaving it grey made
          the whole skeleton read a shade colder than the screen it precedes. */}
      <EbSkeleton className="yop-skel-note" tint />

      {/* ⚠️ FOUR, not a guess. `SECTION_COUNT` is fixed by the blueprint's own
          structure — Financial, Customer, Operations, People — so this is the
          rare skeleton that can promise the exact number of things it draws.
          Only the summaries inside are left blank, because how many WIGs each
          section shortlists is exactly what we are waiting to find out. */}
      {SKELETON_SECTIONS.map((index) => (
        <div className="yop-group yop-skel-group" key={index}>
          <div className="yop-group-head">
            <EbSkeleton className="yop-skel-chevron" i={index} />
            <EbSkeleton className="yop-skel-group-name" i={index} strong />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── The row's shortlist control ──────────────────────────────────────────────
// ⚠️ A BUTTON, replacing the 20px circular checkbox this was until 2026-09-03
// (on instruction: "there is checkbox for shortlist but can we put button
// anywhere in that line for shortlist and remove from shortlist"). Three reasons
// it is the better control here, beyond having been asked for:
//
//   - It says the state AND the action. A ticked circle only implies both, and
//     on this screen the state has a name the reader needs — "shortlisted" is
//     the word the confirm dialog, the header chip and the section summaries all
//     use. A checkbox column made the reader translate a tick into that word
//     twelve times.
//   - Shortlisting is a decision, not a form field. Nothing here is submitted
//     later; each click emits `set_item_shortlist` immediately. A button is what
//     that is.
//   - It gives the row somewhere to put "Remove". A checkbox's untick is silent.
//
// `aria-pressed` rather than `role="checkbox"`/`aria-checked`: this is a toggle
// button now, and a native button with `aria-pressed` is the pattern for one.
//
// ⚠️ THE HOVER LABEL SWAP IS PRESENTATION ONLY. `Shortlisted` → `Remove` on
// hover is done in CSS by crossfading two spans, so it cannot be seen on touch
// and is never announced. That is why `aria-label` states the ACTION in full
// rather than echoing whichever label happens to be painted — a screen reader
// and a phone both get the truth without the hover.
const ShortlistButton = ({ shortlisted, label, disabled, onChange }) => (
  <button
    type="button"
    className={`yop-pick${shortlisted ? " is-in" : ""}`}
    onClick={onChange}
    disabled={disabled}
    aria-pressed={shortlisted}
    aria-label={`${
      shortlisted ? "Remove from" : "Add to"
    } the prioritised WIGs: ${label}`}
  >
    {shortlisted ? (
      <>
        <CheckIcon size={11} />
        <span className="yop-pick-state">Prioritised</span>
        {/* Overlaid on the pair above and revealed on hover — see the note
            about the swap being presentation only. */}
        <span className="yop-pick-undo" aria-hidden="true">
          Remove
        </span>
      </>
    ) : (
      <span className="yop-pick-add">
        <span aria-hidden="true">+</span> Prioritise
      </span>
    )}
  </button>
);

// The locked replacement for the button above. A tag, not a disabled button:
// after `confirm_year1_plan` there is nothing to re-enable, ever, and a greyed
// control invites the click it will refuse.
//
// ⚠️ ONE STATE, AND IT TAKES NO PROPS (2026-09-03). It used to branch on
// `shortlisted` and render a muted "Not shortlisted" caption for the rest. Both
// locked call sites now pass `filterRows` so that only shortlisted rows are
// drawn at all, on instruction — so the other branch had nothing left to render
// and `.yop-tag.is-out` was deleted from the stylesheet with it.
const ShortlistTag = () => (
  <span className="yop-tag">
    <CheckIcon size={11} /> Prioritised
  </span>
);

// ⚠️ `SUMMARY_FACES` AND `GroupSummary` MOVED OUT on 2026-09-03, to
// `_shared/YearOnePlanSections.js`, along with the accordion markup that used
// them — see the note at the section list's call site below. They are not
// re-exported here and nothing in this file needs them: what a shut section
// says is now the shared component's business, and this screen's business is
// only what goes in a row's two right-hand cells.

// The menu's own CSS max-height. Kept in step with `.yop-owner-menu` in the
// stylesheet — it is the height the placement maths has to reason about, and
// measuring the menu instead would mean rendering it in the wrong place first.
const OWNER_MENU_MAX_H = 260;
// Also duplicated from the stylesheet: the menu is `position: fixed`, so its
// `left` has to be computed, and that needs the width.
const OWNER_MENU_W = 220;
const OWNER_MENU_GAP = 6;
// Below this much room, "it fits" is technically true and practically useless —
// a 40px sliver of a scrolling list is worse than flipping.
const OWNER_MENU_MIN_H = 120;
// Breathing room kept between the menu and any viewport edge, so it never looks
// welded to the side of the screen.
const OWNER_MENU_EDGE = 8;

// How many avatars the row shows before collapsing the rest into "+N". Three
// fits the 150px owner column without crowding the name beside it.
const OWNER_AVATAR_LIMIT = 3;

const OwnerCell = ({ row, roster, open, disabled, onToggle, onPick }) => {
  // ── Owners, plural (handoff #25 §4) ──────────────────────────────────────
  // `ownerIds` is the real field; `ownerId` is the first of them, kept because
  // the PDF and anything not yet migrated still read it. Resolved against the
  // roster so a name is shown even for an id the row's own `owners` array
  // hasn't caught up with — the roster is the fresher of the two.
  const ownerIds =
    row.ownerIds && row.ownerIds.length
      ? row.ownerIds
      : row.ownerId != null
        ? [row.ownerId]
        : [];

  const owners = ownerIds
    .map((id) => {
      const member = roster.find((candidate) => candidate.id === id);
      if (member) return { id, name: member.name, initials: member.initials };
      // Not in the roster (left the company mid-session, or the roster is still
      // loading): fall back to whatever name the item itself carried.
      const fromRow = (row.owners || []).find((owner) => owner.user_id === id);
      const name = fromRow?.name || (id === row.ownerId ? row.ownerName : "");
      return name ? { id, name, initials: initialsFor(name) } : null;
    })
    .filter(Boolean);

  // ⚠️ THE LABEL NAMES ONE PERSON OR COUNTS THEM, never a comma list. Three
  // full names do not fit a 150px column, and truncating them mid-name is
  // worse than not printing them: the avatars carry the identities, and hover
  // gives the full list.
  const label = owners.length
    ? owners.length === 1
      ? owners[0].name
      : `${owners.length} owners`
    : "Unassigned";

  const shown = owners.slice(0, OWNER_AVATAR_LIMIT);
  const overflow = owners.length - shown.length;
  const name = owners.length ? label : "";

  // ── Open wherever there is space ──────────────────────────────────────────
  // The menu is `position: fixed` and every coordinate is computed here. That is
  // the fix for it opening into a clipped edge: it sits inside `.yop-scroll`
  // (`overflow-y: auto`) within `#year-one-plan` (`overflow: hidden`), and an
  // absolutely-positioned child is clipped by its scrolling ancestor. The old
  // version measured the VIEWPORT while the container was what actually cut the
  // menu off, so a row far from the bottom of the screen but near the bottom of
  // the scroll box "had room", opened downward, and got sliced. Fixed
  // positioning removes the clipping context, which is what finally makes these
  // viewport measurements true.
  //
  // Both axes are handled: vertical flips above when below is too tight, and
  // horizontal is clamped so neither edge can leave the screen.
  //
  // `useLayoutEffect`, not `useEffect`: it runs between React's commit and the
  // browser's paint, so the menu is never painted in the wrong place and then
  // corrected. The render is also gated on `placement` being set, so there is no
  // first frame at the viewport's top-left.
  const anchorRef = useRef(null);
  const [placement, setPlacement] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      // Cleared on close so the next open measures fresh rather than flashing
      // at wherever this row happened to be last time.
      setPlacement(null);
      return undefined;
    }

    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below =
        window.innerHeight - rect.bottom - OWNER_MENU_GAP - OWNER_MENU_EDGE;
      const above = rect.top - OWNER_MENU_GAP - OWNER_MENU_EDGE;

      // Prefer down, flip only when down genuinely doesn't work AND up is
      // better. Without that second test a cramped row flips into an even
      // smaller gap.
      const flip = below < OWNER_MENU_MIN_H && above > below;
      // Never taller than the room available, never taller than the design's own
      // cap, never so short it's useless. The list scrolls inside whatever it
      // gets.
      const maxHeight = Math.max(
        OWNER_MENU_MIN_H,
        Math.min(OWNER_MENU_MAX_H, flip ? above : below),
      );

      // Right-aligned to the anchor, which is what the old `right: 0` did, then
      // clamped into the viewport so a narrow window can't push it off-screen.
      const rightAligned = rect.right - OWNER_MENU_W;
      const furthestLeft = Math.max(
        OWNER_MENU_EDGE,
        window.innerWidth - OWNER_MENU_W - OWNER_MENU_EDGE,
      );
      const left = Math.min(
        Math.max(OWNER_MENU_EDGE, rightAligned),
        furthestLeft,
      );

      setPlacement({
        above: flip,
        maxHeight,
        left,
        top: flip
          ? rect.top - OWNER_MENU_GAP - maxHeight
          : rect.bottom + OWNER_MENU_GAP,
      });
    };

    measure();

    // Fixed coordinates are absolute to the screen, so they go stale the moment
    // anything scrolls — and the scrolling here happens on `.yop-scroll`, an
    // ANCESTOR. `capture: true` is required for that: scroll events don't
    // bubble, so a listener on `window` only sees them on the way down.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  return (
    <div className="yop-owner" ref={anchorRef}>
      <button
        type="button"
        className={`yop-owner-btn${name ? "" : " is-empty"}`}
        onClick={onToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {/* ── Stacked avatars ──
            Overlapping by a third, newest behind oldest, so several owners read
            as one group rather than a row of separate chips — and so the cell's
            width barely changes between one owner and four.

            Unassigned still shows the person icon, unlike the locked view's
            bare "—": this cell is a PICKER and has to look clickable. */}
        {owners.length ? (
          <span className="yop-avatar-stack" aria-hidden="true">
            {shown.map((owner) => (
              <span className="yop-avatar" key={owner.id}>
                {owner.initials}
              </span>
            ))}
            {overflow > 0 ? (
              <span className="yop-avatar yop-avatar--more">+{overflow}</span>
            ) : null}
          </span>
        ) : (
          <span className="yop-avatar is-empty" aria-hidden="true">
            <PersonIcon />
          </span>
        )}
        {/* `title` carries every name in full — the label counts them once
            there is more than one, and the count is not something you can hover
            an avatar to expand. */}
        <span
          className="yop-owner-name"
          title={
            owners.length > 1 ? owners.map((o) => o.name).join(", ") : undefined
          }
        >
          {label}
        </span>
        {/* ⚠️ NO "AI" BADGE BESIDE THE OWNER (2026-09-03, on instruction).
            This rendered `<span className="yop-owner-ai">AI</span>` whenever
            `row.ownerSuggested` was true — the agent had proposed the owner and
            nobody had confirmed them yet.

            `owner_suggested_by_agent` still arrives on every row and still
            round-trips through the store; it is simply not drawn. The screen's
            own header already says the whole plan is AI-suggested, so a second
            badge per row was repeating that once per owner. */}
      </button>

      {/* Gated on `placement` as well as `open`: the coordinates come from the
          layout effect, and rendering before they exist would paint the menu at
          the viewport's top-left for one frame. */}
      {open && placement && (
        <ul
          className={`yop-owner-menu${placement.above ? " is-above" : ""}`}
          style={{
            top: placement.top,
            left: placement.left,
            maxHeight: placement.maxHeight,
          }}
          // ⚠️ `multiselectable`, and the options are CHECKBOXES rather than
          // `option` roles: a listbox whose entries toggle independently is
          // announced wrongly as single-choice, and the tick is the whole
          // affordance here.
          role="listbox"
          aria-multiselectable="true"
          aria-label={`Owners for ${row.value}`}
        >
          {roster.map((member) => {
            const picked = ownerIds.includes(member.id);
            return (
              <li key={member.id}>
                <button
                  type="button"
                  className={`yop-owner-option${picked ? " is-active" : ""}`}
                  // ⚠️ TOGGLES, AND THE MENU STAYS OPEN. Assigning three people
                  // is one decision made three times; a menu that closed on
                  // each pick would have to be reopened twice, and the row it
                  // reopens from has moved under the new avatars by then.
                  onClick={() => onPick(member.id)}
                  role="checkbox"
                  aria-checked={picked}
                >
                  <span className="yop-owner-check" aria-hidden="true">
                    {picked ? <CheckIcon size={11} /> : null}
                  </span>
                  <span className="yop-avatar" aria-hidden="true">
                    {member.initials}
                  </span>
                  {member.name}
                </button>
              </li>
            );
          })}
          {/* Clears everyone at once. `owner_user_ids: []` is a legitimate
              payload — §4 says so in as many words — and owners you can add but
              not remove wholesale is a trap. Disabled rather than hidden when
              nothing is assigned, so the row does not appear and disappear as
              people are picked. */}
          <li>
            <button
              type="button"
              className="yop-owner-clear"
              onClick={() => onPick(null)}
              disabled={!owners.length}
            >
              Clear all
            </button>
          </li>
          {roster.length === 0 && (
            <li className="yop-owner-empty">
              No one to assign yet — the company roster hasn&apos;t loaded.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

// the disabled button. A constant rather than a literal because both the gate
// and its explanation have to change together.
const CONFIRM_DISABLED_REASON =
  "Prioritise at least one WIG before you lock the blueprint.";

const YearOnePlan = () => {
  const history = useHistory();
  const { blueprintId } = useParams();
  const dispatch = useDispatch();

  const [bannerOpen, setBannerOpen] = useState(true);
  const [ownerMenuFor, setOwnerMenuFor] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  // The WIG awaiting delete confirmation, or null.
  const [pendingDelete, setPendingDelete] = useState(null);

  const snapshot = useLiveReviewSocket(selectSnapshot);
  const joined = useLiveReviewSocket(selectJoined);
  const joining = useLiveReviewSocket(selectJoining);
  const lastError = useLiveReviewSocket(selectLastError);
  const pendingItems = useLiveReviewSocket(selectPendingItems);
  const addPending = useLiveReviewSocket(selectAddPending);
  const locking = useLiveReviewSocket(selectLocking);
  const joinError = useLiveReviewSocket(selectJoinError);

  const clients = useExecutionBlueprintStoreSelector(selectClients);
  const companyName =
    (clients || []).find((c) => c.blueprintId === blueprintId)?.name || "";

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Prioritised WIGs for the next 12 months";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    // eslint-disable-next-line
  }, []);
  // ── "Execution Blueprint › ‹Company› Year 1 Plan" ─────────────────
  // Exactly TWO crumbs, on instruction. The company and the stage are ONE
  // label — "Acme Ltd Year 1 Plan" — because they name a single thing: this
  // client's year one shortlist. Split into separate crumbs they read as a hierarchy with
  // levels you could climb to, and those levels don't exist: the async review
  // is off-limits once the live review has started, and the live review screen
  // itself bounces anyone whose step has moved on.
  //
  // Only the first crumb navigates (the coach's client dashboard). Falls back to
  // a bare "Year 1 Plan" before the dashboard lands, rather than a leading space.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(EXECUTION_BLUEPRINT_YEAR_ONE_PLAN, [
        breadCrumbsArray[EXECUTION_BLUEPRINT][0],
        {
          title: companyName
            ? `${companyName} — Prioritised WIGs for the next 12 months`
            : "Prioritised WIGs for the next 12 months",
          url: "",
        },
      ]),
    );
  }, [dispatch, companyName, blueprintId]);

  // ── Join, and route on the step ───────────────────────────────────────────
  useEffect(() => {
    liveReviewSocketStore
      .getState()
      .connect({ uniqueId: blueprintId, viewerRole: PARTICIPANT_ROLE.COACH });
    return () => liveReviewSocketStore.getState().disconnect();
    // eslint-disable-next-line
  }, [blueprintId]);

  // Escape closes the owner picker. This is the KEYBOARD equivalent of the
  // click-away below — without it, a keyboard user who opens a picker has no way
  // to dismiss it without choosing something, which is worse than the lint rule
  // the click-away trips.
  useEffect(() => {
    if (!ownerMenuFor) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOwnerMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ownerMenuFor]);

  const step = snapshot?.live_review_step;
  const locked = isBlueprintLocked(snapshot);

  // ── The end of the live session ───────────────────────────────────────────
  // `blueprint_locked` is terminal: there is nothing left to emit, nothing left
  // to receive, and the backend derives PRESENCE from the connection — so a
  // socket left open here reports both parties as still in a meeting that
  // finished. On instruction, completing the Year 1 plan now CLOSES the
  // `/live-review` namespace and moves to the Final Locked Blueprint.
  //
  // This reverses an earlier decision (the screen used to stay put as the
  // read-only record). The locked hub is a better record: it survives a
  // refresh, it is reachable days later, and it reads from REST rather than
  // from a room that no longer exists.
  //
  // ⚠️ Order is load-bearing. Navigate FIRST, disconnect SECOND: `disconnect()`
  // resets the store to its initial state, and doing that while this screen is
  // still mounted paints one frame of empty skeleton on the way out. The route
  // change unmounts us first, so nobody sees it.
  //
  // ⚠️ Fires for BOTH parties. The broadcast reaches the coach and the CEO, and
  // each app's copy of this effect does the same thing — which is what keeps
  // them on the same screen at the same moment, exactly as step-routing does.
  const [leftSession, setLeftSession] = useState(false);
  useEffect(() => {
    if (!locked || leftSession) return;
    setLeftSession(true);
    // Straight to the three-tab locked view, on its first tab. This used to
    // land on the "Final Locked Blueprint" hub, which was deleted 2026-09-02 —
    // the confirm popup now hands both parties the record itself rather than a
    // menu in front of it.
    history.replace(
      EXECUTION_BLUEPRINT_LOCKED_DEFAULT.replace(":blueprintId", blueprintId),
    );
    liveReviewSocketStore.getState().disconnect();
  }, [locked, leftSession, history, blueprintId]);

  // Everything except `year1_review` belongs on another screen. Checked in an
  // effect and never inline during render.
  //
  // ⚠️ `locked` is deliberately NOT one of the steps handled here — the lock
  // isn't a step change at all, and it is handled by the effect above.
  useEffect(() => {
    // ⚠️ `year1_drafting` is NOT here, deliberately (2026-08-31). It used to route
    // to a separate "building your Year 1 Plan" screen; that screen was dropped on
    // instruction, and this screen now owns the wait itself — a plain full-screen
    // loading animation until `year1_review` brings the shortlist. Sending the two
    // drafting steps to two different places was also the thing that made this
    // stage feel like three screens instead of one.
    const target = redirectForStep(step, OWN_STEPS, blueprintId);
    if (target) history.replace(target);
  }, [step, blueprintId, history]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const groups = useMemo(() => buildYearOneGroups(snapshot), [snapshot]);
  const roster = useMemo(() => buildRoster(snapshot), [snapshot]);
  const shortlistedCount = countShortlisted(groups);
  const emptyShortlist = isShortlistEmpty(groups);
  const banner = emptyShortlist ? EMPTY_BANNER : AI_BANNER;

  const editable = !locked && joined;

  // ⚠️ AN EMPTY SHORTLIST CANNOT BE LOCKED (2026-09-24, both apps). Confirm was
  // gated only on `editable` and `locking`, so either party could lock the
  // blueprint permanently with NOTHING shortlisted — §6b.7 has no server-side
  // gate in front of `confirm_year1_plan`, and the lock is terminal. That
  // produced a locked Year 1 tab whose own filter (`row.shortlisted`) rendered
  // an empty list, with no way back.
  //
  // ⚠️ THIS DOES NOT CONTRADICT "an empty shortlist is a real answer" at the
  // top of this file. That is about what the AGENT may return — a twice-failed
  // shortlist agent legitimately hands back every item unpicked, and the screen
  // says so in `EMPTY_BANNER` rather than treating it as an error. It is the
  // starting point both parties then build from by hand. What cannot happen is
  // CONFIRMING that state.
  const canConfirm = shortlistedCount > 0;

  // ── Adding and removing WIGs (handoff #25 §2) ─────────────────────────────
  const handleAddItem = (sectionKey, draft) =>
    liveReviewSocketStore.getState().addLiveReviewItem(sectionKey, draft);

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    liveReviewSocketStore.getState().deleteLiveReviewItem(pendingDelete.id);
    setPendingDelete(null);
  };

  // Connectors are already confirmed by the time anyone is on this screen, so
  // removing a WIG here still takes its lines with it — the map is just not on
  // screen to show it. All the more reason to say the number out loud.
  const pendingDeleteConnections = useMemo(() => {
    if (!pendingDelete) return 0;
    const key = String(pendingDelete.id);
    return (snapshot?.connectors?.edges || []).filter(
      (edge) => String(edge.from) === key || String(edge.to) === key,
    ).length;
  }, [pendingDelete, snapshot]);

  const toggleShortlist = (row) =>
    liveReviewSocketStore.getState().setItemShortlist(row.id, !row.shortlisted);

  // The NAME goes with the id, because the store's owner write is optimistic
  // and has to render something immediately — the snapshot only carries
  // `owner_name` for owners the server has already assigned, so a fresh pick
  // would show a blank name until the echo landed. `null` for Unassigned.
  /**
   * Toggle one person on or off a WIG, or clear everyone.
   *
   * ⚠️ THE WIRE TAKES THE FULL LIST, NOT A DELTA (handoff #25 §4), so the
   * toggle is resolved here and the whole new set is sent. `null` is the
   * clear-all case — `owner_user_ids: []`.
   *
   * The menu deliberately stays open. Assigning several people is one decision
   * made several times, and a menu that closed on each pick would have to be
   * reopened for every one of them — from a row whose height has changed under
   * the new avatars in the meantime.
   */
  const pickOwner = (row, ownerId) => {
    const current =
      row.ownerIds && row.ownerIds.length
        ? row.ownerIds
        : row.ownerId != null
          ? [row.ownerId]
          : [];

    const nextIds =
      ownerId == null
        ? []
        : current.includes(ownerId)
          ? current.filter((id) => id !== ownerId)
          : [...current, ownerId];

    // Names travel with the ids so the optimistic write can render initials
    // immediately — the echo replaces them with the server's own a moment later.
    const owners = nextIds.map((id) => {
      const member = roster.find((candidate) => candidate.id === id);
      return { user_id: id, name: member?.name ?? null };
    });

    liveReviewSocketStore.getState().setItemOwner(row.id, owners);
  };

  // ⚠️ The terminal lock. Emits and does NOT navigate, deliberately:
  // `blueprint_locked` is a broadcast to both parties, and leaving is driven by
  // the broadcast arriving (see the effect above), never by the local click.
  // Navigating from here would move whoever pressed the button and leave their
  // counterpart behind — the same rule step-routing follows.
  const handleConfirm = () => {
    // Belt and braces behind the disabled button: the dialog can be open when
    // the OTHER party removes the last shortlisted row, and this lock is
    // permanent with no server-side gate behind it.
    if (!canConfirm) {
      setShowConfirm(false);
      return;
    }
    setShowConfirm(false);
    liveReviewSocketStore.getState().confirmYear1Plan();
  };

  // ⚠️ Error beats skeleton, on instruction: "if error comes we will show error
  // and we will not show loading screen". A skeleton under an error banner
  // promises content that is not coming — the two states together read as
  // "something failed, but keep waiting", which is the one message we never
  // want on a screen two people are watching.

  // ── Don't paint this screen until we know it IS this screen ────────────────
  // `live_review_step` decides which live-review stage the user belongs on, and
  // it arrives with the first snapshot — a beat after mount. Rendering this
  // screen's content before then means anyone arriving mid-journey (or reloading
  // this URL after the step moved) sees THIS stage first and is then redirected:
  // two visible screens for one click, the first one wrong.
  //
  // So the skeleton also covers "the step isn't mine yet". The step-routing
  // effect above does the navigating; this only stops the wrong thing painting
  // while it happens. Same guard as the Live Review screen's, and it must stay
  // in step with the other app's copy.
  //
  // ── TWO DIFFERENT WAITS, AND CONFLATING THEM WAS A BUG ────────────────────
  // ⚠️ Fixed 2026-09-03, reported as "still i am not able to see year one plan
  // loading screen when live_review_step is year1_drafting".
  //
  // Both waits used to come through one flag:
  //
  //   showLoading = !lastError && (!snapshot || !stepIsMine || joining || !joined)
  //
  // and EVERY disjunct there is about data not having arrived yet. But
  // `year1_drafting` is the opposite situation: the snapshot HAS arrived, the
  // room IS joined, and the step is one this screen owns — the agent is simply
  // still writing the shortlist. So the instant the socket settled,
  // `showLoading` went false and the screen fell through to the plan itself,
  // rendering a list with nothing shortlisted in it. The generating animation
  // was only ever on screen for the split second before the join landed, which
  // is exactly why it looked like it never appeared.
  //
  // The two are now separate, and the order below matters:
  //
  //   drafting   → a real, data-backed state the SERVER is in. The step alone
  //                decides it, never the connection. `step` is read off the
  //                snapshot, so no snapshot means no step and this cannot fire
  //                on a failed load.
  //   connecting → we genuinely have nothing to draw: no snapshot, still
  //                joining, or the step belongs to another screen and the
  //                redirect effect above is about to move us.
  //
  // ⚠️ Do not fold these back together. Any future step that means "the server
  // is working" belongs with `drafting`, not with `connecting`.
  const stepIsMine =
    step === LIVE_REVIEW_STEP.YEAR1_REVIEW ||
    step === LIVE_REVIEW_STEP.YEAR1_DRAFTING;
  const showLoadFailed = !snapshot && !!lastError;
  const drafting = step === LIVE_REVIEW_STEP.YEAR1_DRAFTING;
  const connecting =
    !lastError && !drafting && (!snapshot || !stepIsMine || joining || !joined);

  // Whole screen, nothing else. Returned BEFORE the shell rather than inside it
  // so the header, banner and column headings do not sit around an empty table
  // describing content that has not arrived.
  //
  // The error case deliberately does NOT come through here: it still renders
  // inside the full screen, because an error needs its banner and its context to
  // be actionable, whereas a wait needs neither.
  // The agent is running: say so, for as long as it runs.
  if (drafting) return <YearOnePlanLoading joinError={joinError} />;
  // Everything else that isn't ready yet is a handshake, and nothing truthful
  // can be said about it — hence the bare ring rather than a claim.
  if (connecting) return <YearOnePlanConnecting />;

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            EXECUTION_BLUEPRINT_WIG_CONNECTIONS.replace(
              ":blueprintId",
              blueprintId,
            ),
          )
        }
        showBack
      />

      {/* Click-away closes the owner picker. Deliberately NOT given a role:
          this is the absence of a target, not a control, and announcing the
          whole screen as a button would mislead a screen reader. Escape is the
          accessible path and is wired above — which is why this disable is a
          statement rather than a shrug. (The CEO repo's eslint raises these to
          errors where the coach repo doesn't, so it is disabled explicitly in
          both copies to keep them in step.) */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div id="year-one-plan" onClick={() => setOwnerMenuFor(null)}>
        {/* ── ONE HEADER, WHERE THERE WERE THREE ────────────────────────────
            Removed here on 2026-09-03, on instruction ("there are two times
            [the same instruction] ... and there is two titles over there Year 1
            Plan and All Blueprint WIGs which looks weird"):

              - `.yop-list-head` — an <h3> "All Blueprint WIGs" plus a second
                instruction line. The h3 was labelling a list whose own section
                cards already name themselves, so it titled a thing that titles
                itself; and its subtitle said what the line below says, 200px
                apart, in different words. Both gone.
              - `.yop-col-head` — the sticky WIG / OWNER column headings. With a
                labelled button and a named face in every row, the columns
                identify themselves; a sticky bar to say so was chrome over
                content on a screen that is already scrolling.

            What is left is the title, ONE instruction, and the count. */}
        <div className="yop-head">
          <div className="yop-head-text">
            <h2 className="yop-title">Prioritised WIGs for the next 12 months</h2>
            <p className="yop-subtitle">
              {locked
                ? "This blueprint is locked. Below is the plan you agreed."
                : "Pick the WIGs for the next 12 months and give each one an owner. As a guide, aim for about 1.5 WIGs per leadership team member."}
            </p>
          </div>

          {/* Was a 130x55 bordered box reading "5 / WIGs selected". Now a chip,
              for two reasons: it is a live tally rather than a statistic, so it
              belongs in the accent colour the shortlist uses everywhere else on
              this screen; and "shortlisted" is the vocabulary the row buttons
              and section summaries were changed to on 2026-09-03, so "selected"
              was the last place on the screen using the old word.

              Still no "of N" — §6b.4 says there is no expected count and warns
              against hard-coding one. */}
          <span className="yop-count">
            <span className="yop-count-value">{shortlistedCount}</span>
            <span className="yop-count-label">prioritised</span>
          </span>
        </div>

        {lastError && (
          <div className="yop-banner" role="alert">
            <span>{lastError.message}</span>
            <button
              type="button"
              className="yop-banner-btn"
              onClick={() => liveReviewSocketStore.getState().clearError()}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="yop-scroll">
          {bannerOpen && !locked && (
            <div className="yop-ai">
              <span className="yop-ai-icon" aria-hidden="true">
                {GLYPH.sparkle}
              </span>
              <p className="yop-ai-text">
                <b>{banner.lead}</b> {banner.text}
              </p>
              <button
                type="button"
                className="yop-ai-close"
                onClick={() => setBannerOpen(false)}
                aria-label="Dismiss this notice"
              >
                <CloseIcon size={14} />
              </button>
            </div>
          )}

          {showLoadFailed ? (
            // The banner above already carries the message; this says why the
            // table is empty rather than leaving a blank panel under it.
            <div className="yop-loading" role="alert">
              We couldn&apos;t load your prioritised WIGs.
            </div>
          ) : groups.length === 0 ? (
            <div className="yop-loading">
              This blueprint doesn&apos;t have any WIGs to prioritise.
            </div>
          ) : (
            // ⚠️ THE SECTIONS ARE NO LONGER DRAWN HERE (2026-09-03). This
            // was ~90 lines of accordion markup — the section card, its
            // collapsed summary, the row, the label — duplicated in the CEO
            // app's copy of this file and about to be duplicated a third time
            // in the locked view's Year 1 tab ("we also need to same design on
            // /locked/year-one screen"). It moved to
            // `_shared/YearOnePlanSections.js`, which all three now render.
            //
            // `collapsed` state moved with it: the component owns it, because
            // all three callers want the same behaviour and none of them reads
            // it. This screen keeps the two things that ARE its own — a row's
            // shortlist control and its owner cell — and hands them over as
            // render props.
            <YearOnePlanSections
              groups={groups}
              isPending={(row) => !!pendingItems[String(row.id)]}
              // ⚠️ ONCE LOCKED, ONLY THE SHORTLIST IS LISTED — the same rule
              // the locked hub's Year 1 tab follows, and stated twice on
              // instruction ("only shortlisted will show on locked screen").
              //
              // This branch is very nearly unreachable: `blueprint_locked`
              // redirects both parties to the locked hub within a frame (see the
              // effect above), so this is what shows for that one frame and for
              // anyone whose redirect has not landed. It is filtered anyway,
              // because "a locked Year 1 Plan lists what was chosen" is a
              // property of the feature, not of one screen — and an unreachable
              // branch that disagrees with the reachable one is exactly how the
              // two drift apart the next time someone makes this state
              // reachable.
              //
              // `filterRows` narrows the body only; the section summaries still
              // read "2 of 3 shortlisted" against every row. See that prop in
              // YearOnePlanSections.js.
              filterRows={locked ? (row) => row.shortlisted : undefined}
              // ── Add / remove a WIG (handoff #25 §2) ──
              // Both are `undefined` when the blueprint is locked, so the
              // shared renderer draws nothing at all rather than a disabled
              // control — see its own note on why these are render props.
              // `editable` is already `!locked && joined`, so a disconnected
              // socket takes them away too: an add row that emits into a closed
              // socket would look like it had worked.
              renderAddRow={
                editable
                  ? (group) => (
                      <AddWigRow
                        sectionKey={group.key}
                        sectionLabel={group.label}
                        count={group.rows.length}
                        pending={addPending === group.key}
                        onAdd={handleAddItem}
                      />
                    )
                  : undefined
              }
              renderRowActions={
                editable
                  ? (row, group) =>
                      canDeleteLiveReviewItem(group.rows.length) ? (
                        <button
                          type="button"
                          className="yop-row-remove"
                          title="Remove this WIG"
                          aria-label={`Remove ${row.value}`}
                          disabled={!!pendingItems[String(row.id)]}
                          onClick={(event) => {
                            // The row is a click target in its own right on this
                            // screen — it toggles the shortlist — so a bare
                            // click here would shortlist the WIG on its way to
                            // asking whether to delete it.
                            event.stopPropagation();
                            setPendingDelete(row);
                          }}
                        >
                          <TrashIcon size={13} />
                        </button>
                      ) : null
                  : undefined
              }
              renderControl={(row) =>
                locked ? (
                  <ShortlistTag />
                ) : (
                  <ShortlistButton
                    shortlisted={row.shortlisted}
                    label={row.value}
                    disabled={!editable || !!pendingItems[String(row.id)]}
                    onChange={() => toggleShortlist(row)}
                  />
                )
              }
              renderOwner={(row) => (
                // `stopPropagation` wraps ONLY the owner cell, not the control
                // beside it. The screen-level click-away that closes this menu
                // lives on `#year-one-plan`, so the picker's own toggle has to
                // stop the event or it would close itself on the way up — while
                // a click on the shortlist button SHOULD close an open picker,
                // which is what leaving that outside this wrapper preserves.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                  className="yop-row-owner"
                  onClick={(event) => event.stopPropagation()}
                  role="presentation"
                >
                  {/* ⚠️ NOT disabled by `pending`, unlike the shortlist button
                      beside it. The owner write is optimistic, so the new name
                      is already on screen; freezing the control until the echo
                      arrived would take away the responsiveness that change
                      exists for — and stop a misclick being fixed straight
                      away. */}
                  <OwnerCell
                    row={row}
                    roster={roster}
                    open={ownerMenuFor === row.id}
                    disabled={!editable}
                    onToggle={() =>
                      setOwnerMenuFor((prev) =>
                        prev === row.id ? null : row.id,
                      )
                    }
                    onPick={(ownerId) => pickOwner(row, ownerId)}
                  />
                </div>
              )}
            />
          )}
        </div>

        {/* No "Back to Connections" button — removed on instruction
            (2026-08-31), on both sides. Year 1 Plan is a one-way door.

            It was never as useful as it looked: by the time this screen is up
            the connectors have been confirmed and `wigs_locked` is set, so the
            connectors screen is read-only, and its own step gate bounces anyone
            whose `live_review_step` has moved on — which by definition it has.
            So the button offered a trip to a screen that would refuse to keep
            you.

            The footer keeps `justify-content: flex-end` so the Confirm button
            stays where it was rather than sliding left into the gap. */}
        <div className="yop-footer">
          {locked ? (
            <span className="yop-locked-tag">
              <CheckIcon size={14} /> Blueprint locked
            </span>
          ) : (
            <>
              {/* ⚠️ THE DISABLED BUTTON EXPLAINS ITSELF, THROUGH THE TOOLTIP
                  THIS FEATURE ALREADY USES. `ElegantTooltip` wrapping a
                  `<span>` around a disabled button is exactly how the live
                  review's own Lock Blueprint badge states its reason — the
                  same component and the same wrapper trick, so the two
                  terminal controls refuse identically.
                  ⚠️ THE SPAN IS LOAD-BEARING. A disabled button fires no mouse
                  events in any browser, so a tooltip bound to the button itself
                  would never open. The wrapper is what receives the hover. */}
              {canConfirm ? (
                <button
                  type="button"
                  className="yop-confirm-btn"
                  onClick={() => setShowConfirm(true)}
                  disabled={!editable || locking}
                >
                  <CheckIcon size={16} />
                  {locking ? "Locking…" : "Confirm and lock"}
                </button>
              ) : (
                <ElegantTooltip
                  content={CONFIRM_DISABLED_REASON}
                  placement="top"
                >
                  <span className="yop-confirm-btn-wrap">
                    <button type="button" className="yop-confirm-btn" disabled>
                      <CheckIcon size={16} />
                      Confirm and lock
                    </button>
                  </span>
                </ElegantTooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* The design's stage-3 popup — Figma node 33831:49650, built as
          `ConfirmStepModal`'s CONFIRM_YEAR_ONE_PLAN preset. Its completion note
          ("All three stages of the Live Review are complete") is now literally
          true, and its "Confirm & Lock Blueprint" label finally means it.

          ⚠️ The subtitle is OVERRIDDEN to say the lock is permanent. §6b.7 is
          explicit that there is NO server-side gate in front of this, so this
          dialog is the last thing between a stray click and a permanently
          locked blueprint — the preset's own wording describes confirming a
          selection, which is not what this does. */}
      {showConfirm && (
        <ConfirmStepModal
          {...CONFIRM_YEAR_ONE_PLAN}
          subtitle="This locks the whole blueprint: every WIG, the 12-month priorities and their owners. Nobody can change it afterwards."
          confirmLabel="Lock blueprint"
          loadingLabel="Locking…"
          confirming={locking}
          onConfirm={handleConfirm}
          onBack={() => setShowConfirm(false)}
          onClose={() => setShowConfirm(false)}
        />
      )}

      {pendingDelete && (
        <DeleteWigDialog
          item={pendingDelete}
          connectionCount={pendingDeleteConnections}
          busy={!!pendingItems[String(pendingDelete.id)]}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default YearOnePlan;
